import React from 'react';
import { cn } from '@/lib/utils';
import { CircularProgress } from './CircularProgress';
import { TaskReportSection } from './TaskReportSection';
import { TaskReportCardProps } from './types';

const DEFAULT_SCHEDULE_STATUS = 'All Tasks Completed';
const DEFAULT_SCHEDULE_MESSAGE = 'You are all caught up for today!';
const DEFAULT_TASKS_COMPLETED = 10;
const DEFAULT_TASKS_MAX = 10;
const DEFAULT_TIME_SAVED = 45;
const DEFAULT_TIME_UNIT = 'min';
const DEFAULT_TIME_MAX = 60;
const DEFAULT_LEADERBOARD_RANK = 2;
const DEFAULT_LEADERBOARD_TOTAL = 10;
const DEFAULT_WAITING_MESSAGE = 'Waiting for new tasks...';

const TaskReportCard: React.FC<TaskReportCardProps> = ({
  scheduleStatus = DEFAULT_SCHEDULE_STATUS,
  scheduleMessage = DEFAULT_SCHEDULE_MESSAGE,
  tasksCompleted = DEFAULT_TASKS_COMPLETED,
  tasksCompletedMax = DEFAULT_TASKS_MAX,
  timeSaved = DEFAULT_TIME_SAVED,
  timeSavedUnit = DEFAULT_TIME_UNIT,
  timeSavedMax = DEFAULT_TIME_MAX,
  leaderboardRank = DEFAULT_LEADERBOARD_RANK,
  leaderboardTotal = DEFAULT_LEADERBOARD_TOTAL,
  waitingMessage = DEFAULT_WAITING_MESSAGE,
  className = '',
}) => {
  return (
    <article
      className={cn(
        'bg-white rounded-lg shadow-sm border border-gray-100 p-6 w-full max-w-sm',
        className
      )}
      role="region"
      aria-label="Task Performance Report"
    >
      <header className="text-center mb-6 pb-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-sky-700 mb-2">{scheduleStatus}</h2>
        <p className="text-sm font-light text-gray-600">{scheduleMessage}</p>
      </header>

      <TaskReportSection
        title="Total Tasks Completed"
        description="You've completed all your scheduled tasks for today."
        className="border-b border-gray-100"
      >
        <CircularProgress value={tasksCompleted} maxValue={tasksCompletedMax}>
          <span className="text-xl font-bold text-gray-800">{tasksCompleted}</span>
        </CircularProgress>
      </TaskReportSection>

      <TaskReportSection
        title="Time Saved with Pixie AI"
        description="AI assistance made you more efficient"
        className="border-b border-gray-100"
      >
        <CircularProgress value={timeSaved} maxValue={timeSavedMax}>
          <div className="text-center">
            <span className="text-xl font-bold text-gray-800">{timeSaved}</span>
            <span className="text-xs font-light text-gray-600 block">{timeSavedUnit}</span>
          </div>
        </CircularProgress>
      </TaskReportSection>

      <TaskReportSection
        title="Leaderboard • Weekly Rank"
        description="You've performed exceptionally well this week."
        className="border-b border-gray-100"
      >
        <CircularProgress value={leaderboardRank} maxValue={leaderboardTotal}>
          <span className="text-xl font-bold text-gray-800">
            {leaderboardRank}/{leaderboardTotal}
          </span>
        </CircularProgress>
      </TaskReportSection>

      <footer className="text-center pt-4">
        <p className="text-sm font-light text-gray-500">{waitingMessage}</p>
      </footer>
    </article>
  );
};

TaskReportCard.displayName = 'TaskReportCard';

export { TaskReportCard };
